import { formatYearMonth, hf, TEARSHEET_THEMES, type TearsheetTheme, type TearsheetThemeId } from '@hf/designSystem/v1';
import { SHOW_FAVORITE_LAYOUT_COUNT } from '@hf/sharedContract';
import { ADDABLE_SECTIONS, copyLayoutName, SECTION_LABELS, STOCK_LAYOUT_OWNER, WIDTH_OPTIONS } from '../model/layoutModel';
import { defaultReportMonthRange, shiftYearMonth } from '../model/reportBoardModel';

/** Seed data for the landing tour. Nothing here is fetched — the tour never calls product APIs. */

export type DemoStatus = 'missing' | 'draft' | 'submitted' | 'approved' | 'published';

export type DemoReportRow = {
  id: string;
  fundId: string;
  fundName: string;
  asOf: string;
  status: DemoStatus;
  layout: string;
  owner: string;
};

export type StencilId = 'header' | 'kpis' | 'vami' | 'monthly' | 'risk' | 'drawdowns' | 'commentary' | 'disclaimer';

/** Same 4-track grid as the real builder (`GRID_COLUMNS`): each column is a quarter of the page. */
export type GridItem = { id: StencilId; col: number; row: number; cols: number; rows: number };

export const GRID_COLUMNS = 4;

const { fromMonth, toMonth } = defaultReportMonthRange();

export const DEMO_RANGE = { from: formatYearMonth(fromMonth), to: formatYearMonth(toMonth) };

export const DEMO_FUNDS = [
  { fundId: '101', fundName: 'Northstar Multi-Strategy' },
  { fundId: '102', fundName: 'Harbor Equity Neutral' },
  { fundId: '103', fundName: 'Fund 123456789' },
] as const;

export const DEMO_CREATE_FUND = DEMO_FUNDS[2];
export const DEMO_CREATE_AS_OF = formatYearMonth(shiftYearMonth(toMonth, -1));

const BOARD_PLAN: Record<string, Array<{ status: DemoStatus; layout: string; owner: string }>> = {
  '101': [
    { status: 'submitted', layout: 'Modern', owner: 'Priya Shah' },
    { status: 'published', layout: 'Executive', owner: 'Alex Chen' },
    { status: 'published', layout: 'Executive', owner: 'Alex Chen' },
  ],
  '102': [
    { status: 'approved', layout: 'Institutional', owner: 'Jane Rivera' },
    { status: 'draft', layout: 'Institutional', owner: 'Jane Rivera' },
    { status: 'published', layout: 'Modern', owner: 'Priya Shah' },
  ],
  '103': [
    { status: 'missing', layout: '—', owner: '—' },
    { status: 'published', layout: 'Executive', owner: 'Jordan Lee' },
    { status: 'published', layout: 'Executive', owner: 'Jordan Lee' },
  ],
};

export const DEMO_BOARD: DemoReportRow[] = DEMO_FUNDS.flatMap((fund) =>
  (BOARD_PLAN[fund.fundId] ?? []).map((row, index) => ({
    id: `${fund.fundId}-${index}`,
    fundId: fund.fundId,
    fundName: fund.fundName,
    asOf: formatYearMonth(shiftYearMonth(toMonth, -(index + 1))),
    ...row,
  })),
);

export const DEMO_MISSING_ROW_ID = '103-0';

/** Mirrors `STATUS_FILTERS` on the Reports page. */
export const DEMO_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'missing', label: 'Missing' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
] as const;

export const STENCIL_LABELS = Object.fromEntries(
  (['header', 'kpis', 'vami', 'monthly', 'risk', 'drawdowns', 'commentary', 'disclaimer'] as const).map((id) => [id, SECTION_LABELS[id] ?? id]),
) as Record<StencilId, string>;

export const DEMO_ADDABLE = [...ADDABLE_SECTIONS].sort((a, b) => a.label.localeCompare(b.label));
export const DEMO_ADD_QUERY = 'draw';

export const DEMO_WIDTHS = WIDTH_OPTIONS.map((option) => ({ label: option.label, cols: option.colSpan / 3 }));

export const EXECUTIVE_ITEMS: GridItem[] = [
  { id: 'header', col: 1, row: 1, cols: 4, rows: 1 },
  { id: 'kpis', col: 1, row: 2, cols: 4, rows: 1 },
  { id: 'vami', col: 1, row: 3, cols: 2, rows: 3 },
  { id: 'risk', col: 3, row: 3, cols: 2, rows: 3 },
  { id: 'commentary', col: 1, row: 6, cols: 4, rows: 2 },
  { id: 'monthly', col: 1, row: 8, cols: 4, rows: 2 },
  { id: 'disclaimer', col: 1, row: 10, cols: 4, rows: 1 },
];

export const MODERN_ITEMS: GridItem[] = [
  { id: 'header', col: 1, row: 1, cols: 4, rows: 1 },
  { id: 'kpis', col: 1, row: 2, cols: 4, rows: 1 },
  { id: 'vami', col: 1, row: 3, cols: 2, rows: 3 },
  { id: 'commentary', col: 3, row: 3, cols: 2, rows: 3 },
  { id: 'risk', col: 1, row: 6, cols: 2, rows: 3 },
  { id: 'monthly', col: 1, row: 9, cols: 4, rows: 2 },
  { id: 'disclaimer', col: 1, row: 11, cols: 4, rows: 1 },
];

/** Modern after the tour: VAMI ↔ Program Description swapped, Drawdown Table added in the empty cell and widened to 1/2. */
export const CUSTOM_ITEMS: GridItem[] = MODERN_ITEMS.map((item) =>
  item.id === 'vami' ? { ...item, col: 3 } : item.id === 'commentary' ? { ...item, col: 1 } : item,
).concat({ id: 'drawdowns', col: 3, row: 6, cols: 2, rows: 3 });

export function gridRowCount(items: GridItem[]) {
  return items.reduce((max, item) => Math.max(max, item.row + item.rows - 1), 1);
}

export function placeItem(items: GridItem[], id: StencilId, patch: Partial<GridItem>): GridItem[] {
  return items.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

export type DemoLayoutChoice = { id: string; name: string; owner: string; items: GridItem[] };

export const DEMO_STOCK_LAYOUTS: DemoLayoutChoice[] = [
  { id: 'executive', name: 'Executive', owner: STOCK_LAYOUT_OWNER, items: EXECUTIVE_ITEMS },
  { id: 'institutional', name: 'Institutional', owner: STOCK_LAYOUT_OWNER, items: EXECUTIVE_ITEMS },
  { id: 'modern', name: 'Modern', owner: STOCK_LAYOUT_OWNER, items: MODERN_ITEMS },
];
export const DEMO_CLIENT_LAYOUT: DemoLayoutChoice = { id: 'client-board', name: 'Client board', owner: 'Jordan Lee', items: EXECUTIVE_ITEMS };
export const DEMO_CUSTOM_LAYOUT_ID = 'larkspur-monthly';
export const DEMO_CUSTOM_LAYOUT_NAME = 'Larkspur Monthly';
export const DEMO_COPY_NAME = copyLayoutName('Modern');
export const DEMO_FAVORITE_SLOTS = SHOW_FAVORITE_LAYOUT_COUNT;

export const DEMO_SKIN_ORDER: TearsheetThemeId[] = ['institutional', 'slate', 'black_orange', 'grey_orange', 'traditional'];
export const CUSTOM_SKIN_ID = 'custom';

export type DemoSkin = TearsheetTheme & { name: string };

export const NEW_SKIN_DRAFT: DemoSkin = { name: 'New skin', ...TEARSHEET_THEMES.institutional };

/** The skin the cursor builds in Skins → Create new. Brand navy + logo cyan. */
export const LARKSPUR_SKIN: DemoSkin = {
  name: 'Larkspur Navy',
  bg: hf.navy900,
  panel: hf.navy600,
  panel2: hf.navy500,
  text: hf.white,
  muted: hf.coolGray200,
  accent: hf.cyan300,
  border: hf.navy400,
};

export const SKIN_TOKENS = ['bg', 'panel', 'panel2', 'text', 'muted', 'accent', 'border'] as const;
export type SkinToken = (typeof SKIN_TOKENS)[number];
export const SKIN_TOKEN_LABELS: Record<SkinToken, string> = {
  bg: 'Background',
  panel: 'Panel',
  panel2: 'Panel 2',
  text: 'Text',
  muted: 'Muted',
  accent: 'Accent',
  border: 'Border',
};

export const DEMO_ADVISOR = 'Larkspur Ridge Partners';
export const DEMO_SUBTITLE = 'Monthly Tearsheet';
export const DEMO_PM_NAME = 'Jordan Lee';
export const DEMO_DISCLAIMER = 'Past performance is not indicative of future results. For qualified investors only.';
export const DEMO_PM_BIO = 'Jordan runs the relative-value book with a focus on disciplined risk budgeting.';
export const DEMO_INDEXES = ['S&P 500 INDEX', 'SPDR S&P 500 ETF', 'S&P GSCI'];
export const DEMO_BENCHMARK_QUERY = 'S&P';
export const DEMO_FONTS = [
  { value: 'arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
  { value: 'georgia', label: 'Georgia', stack: 'Georgia, "Times New Roman", serif' },
  { value: 'verdana', label: 'Verdana', stack: 'Verdana, Tahoma, sans-serif' },
] as const;

const MONTHLY_RETURNS = [0.6, -1.2, 1.7, 0.9, 0.4, -0.3, 1.1, 1.9, 0.7, -0.8, 1.4, 1.3];

export const DEMO_MONTHLY = MONTHLY_RETURNS.map((value, index) => ({
  label: formatYearMonth(shiftYearMonth(toMonth, index - MONTHLY_RETURNS.length)).slice(0, 3),
  value,
}));

export const DEMO_VAMI = [1.1, 0.4, -0.6, 2.1, 0.8, ...MONTHLY_RETURNS].reduce<number[]>(
  (acc, pct) => [...acc, (acc[acc.length - 1] ?? 1000) * (1 + pct / 100)],
  [1000],
);

export const DEMO_KPIS = [
  { label: 'Ending equity', value: '$10.2M' },
  { label: 'YTD', value: '+4.82%' },
  { label: 'MTD', value: '+1.30%' },
  { label: 'Since inception', value: '+68.1%' },
];

/** Performance Statistics rows — the Edit modal picks and orders these. */
export const DEMO_STATS: Record<string, string> = {
  CAGR: '+9.40%',
  YTD: '+4.82%',
  'Avg. Monthly Return': '+0.71%',
  'Sharpe Ratio': '0.87',
  'Sortino Ratio': '1.12',
  'Max. Drawdown': '-8.40%',
  GSD: '8.40%',
  'Calmar Ratio': '1.05',
};
export const DEMO_STAT_OPTIONS = Object.keys(DEMO_STATS);
export const DEMO_STAT_ROWS = ['CAGR', 'YTD', 'Avg. Monthly Return', 'Sharpe Ratio', 'Sortino Ratio'];
export const EDITED_STAT_ROWS = ['CAGR', 'Sharpe Ratio', 'Sortino Ratio', 'Max. Drawdown', 'Calmar Ratio'];

export const DEMO_DRAWDOWNS = [
  { start: 'Apr 2024', length: '3 mo', depth: -8.4 },
  { start: 'Oct 2022', length: '2 mo', depth: -5.1 },
  { start: 'Mar 2020', length: '1 mo', depth: -3.2 },
  { start: 'Jan 2019', length: '2 mo', depth: -2.6 },
];

export const DEMO_COMMENTARY =
  'The fund gained in a volatile month as disciplined risk controls limited drawdown while relative-value books captured spread normalization.';
